import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatAxisKr } from '../bills/CostHistoryChart';
import { periodLabel, periodShortLabel, tickInterval, toKr } from '../bills/cost-history';
import {
  axisProps,
  CHART_MARGIN,
  formatWholeKr,
  gridProps,
  tooltipContentStyle,
  tooltipLabelStyle,
  useIsSmUp,
} from '../insights/chart';
import type { SavingsPoint } from './savings-history';

type Datum = {
  key: string;
  year: number;
  label: string;
  shortLabel: string;
  /** Net in whole kronor; negative when more was taken out than put in */
  value: number;
  deposited: number;
  withdrawn: number;
  isCurrent: boolean;
};

const CURRENT_FILL = 'var(--primary)';
const PAST_FILL = 'var(--chart-2)';
const NEGATIVE_FILL = 'var(--negative)';

const fillFor = (d: { value: number; isCurrent: boolean }) =>
  d.value < 0 ? NEGATIVE_FILL : d.isCurrent ? CURRENT_FILL : PAST_FILL;

/** Round steps covering [min, max] and always 0, e.g. −1 000, 0, 1 000, 2 000 */
function signedTicks(min: number, max: number): number[] {
  const low = Math.min(min, 0);
  const high = Math.max(max, 0);
  if (high - low <= 0) return [0, 100];
  const raw = (high - low) / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? raw;
  const bottom = Math.floor(low / step) * step;
  const top = Math.ceil(high / step) * step;
  return Array.from({ length: Math.round((top - bottom) / step) + 1 }, (_, i) => bottom + i * step);
}

function toData(points: SavingsPoint[]): Datum[] {
  const lastIndex = points.length - 1;
  return points.map((p, index) => ({
    key: `${p.year}-${p.month}`,
    year: p.year,
    label: periodLabel(p.year, p.month),
    shortLabel: periodShortLabel(p.month),
    value: toKr(p.net),
    deposited: toKr(p.deposited),
    withdrawn: toKr(p.withdrawn),
    isCurrent: index === lastIndex,
  }));
}

/**
 * Net saved per budget period: bars above zero for money put away, below
 * zero (in the negative tone) when more was taken out. The ongoing period
 * is highlighted and a dashed line marks the average.
 */
export function SavingsChart({
  points,
  average,
  height,
  ariaLabel,
}: {
  points: SavingsPoint[];
  /** Öre, drawn as a reference line */
  average: number | null;
  height: { base: number; sm: number };
  ariaLabel: string;
}) {
  const isSmUp = useIsSmUp();
  const data = toData(points);
  const lastIndex = data.length - 1;
  const values = data.map((d) => d.value);
  const avgKr = average != null ? toKr(average) : 0;
  const yTicks = signedTicks(Math.min(0, ...values, avgKr), Math.max(0, ...values, avgKr));
  const hasNegative = values.some((v) => v < 0);
  const byKey = new Map(data.map((d) => [d.key, d]));
  // Thin the ticks from the newest period backwards so the current one is always labelled
  const step = tickInterval(data.length, isSmUp) + 1;
  const ticks = data.filter((_, index) => (lastIndex - index) % step === 0).map((d) => d.key);
  const yearMarks = new Set(
    data.length > 12
      ? ticks.filter((key, i) => byKey.get(key)?.year !== byKey.get(ticks[i - 1] ?? '')?.year)
      : [],
  );

  return (
    <div className="space-y-2">
      <div role="img" aria-label={ariaLabel}>
        <ResponsiveContainer width="100%" height={isSmUp ? height.sm : height.base}>
          <BarChart data={data} margin={CHART_MARGIN} barCategoryGap="18%">
            <CartesianGrid {...gridProps} vertical={false} />
            <XAxis
              {...axisProps}
              dataKey="key"
              ticks={ticks}
              interval={0}
              tickFormatter={(key: string) => {
                const d = byKey.get(key);
                if (!d) return '';
                if (isSmUp && data.length <= 6) return d.label;
                return yearMarks.has(key)
                  ? `${d.shortLabel} ’${String(d.year).slice(2)}`
                  : d.shortLabel;
              }}
            />
            <YAxis
              {...axisProps}
              width={isSmUp ? 48 : 40}
              domain={[yTicks[0] ?? 0, yTicks.at(-1) ?? 'auto']}
              ticks={yTicks}
              tickFormatter={formatAxisKr}
            />
            <Tooltip
              cursor={{ fill: 'var(--muted)', opacity: 0.6 }}
              content={({ active, payload }) => {
                const d = payload?.[0]?.payload as Datum | undefined;
                if (!active || !d) return null;
                return (
                  <div style={{ ...tooltipContentStyle, padding: '8px 10px' }}>
                    <div style={tooltipLabelStyle}>
                      {d.label}
                      {d.isCurrent ? ' · pågår' : ''}
                    </div>
                    <div className="tabular-nums font-medium">
                      {d.value > 0 ? '+' : ''}
                      {formatWholeKr(d.value)}
                    </div>
                    {d.withdrawn > 0 && (
                      <div style={tooltipLabelStyle} className="tabular-nums">
                        in {formatWholeKr(d.deposited)} · ut {formatWholeKr(d.withdrawn)}
                      </div>
                    )}
                  </div>
                );
              }}
            />
            {hasNegative && <ReferenceLine y={0} stroke="var(--muted-foreground)" />}
            {average != null && average !== 0 && (
              <ReferenceLine
                y={avgKr}
                stroke="var(--muted-foreground)"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
              />
            )}
            <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={32} isAnimationActive={false}>
              {data.map((d) => (
                <Cell key={d.key} fill={fillFor(d)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span
            aria-hidden
            className="size-2.5 rounded-sm"
            style={{ backgroundColor: CURRENT_FILL }}
          />
          Pågående period
        </span>
        {hasNegative && (
          <span className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className="size-2.5 rounded-sm"
              style={{ backgroundColor: NEGATIVE_FILL }}
            />
            Mer uttaget än insatt
          </span>
        )}
        {average != null && average !== 0 && (
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden className="w-3 border-t-2 border-dashed border-muted-foreground" />
            Snitt {formatWholeKr(avgKr)}
          </span>
        )}
      </div>
    </div>
  );
}

/** Tiny net-per-period bars for a pot card; zero periods get a faint baseline tick */
export function SavingsSparkline({ points }: { points: SavingsPoint[] }) {
  const data = toData(points);
  const max = Math.max(0, ...data.map((d) => d.value));
  const min = Math.min(0, ...data.map((d) => d.value));
  const span = max - min || 1;
  const marker = span * 0.06;
  const rows = data.map((d) => ({
    ...d,
    bar: d.value !== 0 ? d.value : null,
    empty: d.value === 0 ? marker : null,
  }));

  return (
    <div aria-hidden className="pointer-events-none">
      <ResponsiveContainer width="100%" height={36}>
        <BarChart
          data={rows}
          margin={{ top: 2, right: 0, bottom: 0, left: 0 }}
          barCategoryGap="14%"
        >
          <YAxis hide domain={[min, max > 0 ? max : min < 0 ? 0 : 1]} />
          {min < 0 && <ReferenceLine y={0} stroke="var(--border)" />}
          <Bar dataKey="bar" stackId="s" isAnimationActive={false}>
            {rows.map((d) => (
              <Cell key={d.key} fill={fillFor(d)} />
            ))}
          </Bar>
          <Bar dataKey="empty" stackId="s" fill="var(--border)" isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
